// Tab bar glyphs for the iPhone shell. Paths are taken from the approved
// iPhone prototype boards so the bar reads the same on every screen.
import type { SVGProps } from 'react';

type GlyphProps = SVGProps<SVGSVGElement> & { size?: number };

function Stroke({ size = 24, children, ...props }: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

/** Today: a sun rising over the horizon. */
export function SunriseGlyph(props: GlyphProps) {
  return <Stroke data-glyph="sunrise" {...props}><path d="M12 3v3M4.9 7.9 7 10M19.1 7.9 17 10M2.5 16h19M6.5 16a5.5 5.5 0 0 1 11 0M8 20h8" /></Stroke>;
}

/** Training: a kettlebell. */
export function KettlebellGlyph(props: GlyphProps) {
  return <Stroke data-glyph="kettlebell" {...props}><path d="M8.5 9a3.5 3.5 0 1 1 7 0" /><path d="M6.8 9h10.4l1.3 9.2a2 2 0 0 1-2 2.3H7.5a2 2 0 0 1-2-2.3z" /></Stroke>;
}

/** Nutrition: fork and knife. */
export function ForkKnifeGlyph(props: GlyphProps) {
  return <Stroke data-glyph="fork-knife" {...props}><path d="M5 3v6a2 2 0 0 0 4 0V3M7 3v18M17 21V3c-2.2 1.4-3.3 4-3.3 7.4 0 2.2 1.1 3.3 3.3 3.3" /></Stroke>;
}

/** Faith: a plain cross. */
export function CrossGlyph(props: GlyphProps) {
  return <Stroke data-glyph="cross" {...props}><path d="M12 3v18M6.5 8.5h11" /></Stroke>;
}

/** More: three dots. */
export function MoreDotsGlyph({ size = 24, ...props }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" data-glyph="more-dots" {...props}>
      <circle cx="5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="19" cy="12" r="1.8" />
    </svg>
  );
}

/** Coach Command: a shield with a check. */
export function ShieldCheckGlyph(props: GlyphProps) {
  return <Stroke data-glyph="shield-check" {...props}><path d="M12 3 4 6v6c0 4.5 3.4 8 8 9 4.6-1 8-4.5 8-9V6z" /><path d="m9 12 2 2 4-4" /></Stroke>;
}

/** Coach Clients: two people. */
export function ClientsGlyph(props: GlyphProps) {
  return <Stroke data-glyph="clients" {...props}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.8-3.5 3.3-5.5 6.5-5.5s5.7 2 6.5 5.5" /><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14.8c1.9.7 3.1 2.4 3.5 5.2" /></Stroke>;
}

/** Coach Inbox: an inbox tray. */
export function InboxGlyph(props: GlyphProps) {
  return <Stroke data-glyph="inbox" {...props}><path d="M4 13h4l2 3h4l2-3h4" /><path d="M5.5 5h13l1.5 8v6H4v-6z" /></Stroke>;
}

/** Coach Schedule: a calendar. */
export function CalendarGlyph(props: GlyphProps) {
  return <Stroke data-glyph="calendar" {...props}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></Stroke>;
}

/** Header: Challenge HQ trophy. */
export function TrophyGlyph(props: GlyphProps) {
  return <Stroke data-glyph="trophy" {...props}><path d="M8 4h8v5a4 4 0 0 1-8 0z" /><path d="M8 6H4v1a4 4 0 0 0 4 4M16 6h4v1a4 4 0 0 1-4 4M12 13v4M8 21h8" /></Stroke>;
}

/** Header: message bubble. */
export function MessageGlyph(props: GlyphProps) {
  return <Stroke data-glyph="message" {...props}><path d="M4 5h16v11H9l-5 4z" /></Stroke>;
}

/** Header: back chevron. */
export function BackChevronGlyph(props: GlyphProps) {
  return <Stroke strokeWidth={2.2} data-glyph="back" {...props}><path d="m15 5-7 7 7 7" /></Stroke>;
}
